'use client'

import { Select as SelectPrimitive } from '@base-ui/react/select'
import { CaretDownIcon, CheckIcon } from '@/components/icons/icon-set'
import { cn } from '@/lib/utils'

/*
 * GoodUnited / Select. Figma: 10 / Components / Select (closed trigger) and Field/Select.
 * Trigger matches Input: 40px, 12px padding, Roboto / text-base, 20px caret in Colors/icon/secondary.
 * size sm is the compact 32px trigger used in card footers and table pagination.
 * The open list reuses the DropdownMenu surface (Colors/surface/overlay, 8px corners).
 */
const Select = SelectPrimitive.Root

type SelectTriggerProps = Omit<SelectPrimitive.Trigger.Props, 'className'> & {
    className?: string
    size?: 'sm' | 'default'
}

function SelectTrigger({ className, size = 'default', children, ...props }: SelectTriggerProps) {
    return (
        <SelectPrimitive.Trigger
            data-slot='select-trigger'
            data-size={size}
            className={cn(
                'flex w-full min-w-0 cursor-pointer items-center justify-between gap-2 rounded-md border border-input-border bg-input-background text-left text-input-foreground transition-colors outline-none hover:border-input-border-hover focus-visible:border-input-border-focus data-disabled:cursor-not-allowed data-disabled:opacity-50 data-popup-open:border-input-border-focus',
                size === 'default' ? 'h-10 px-3 text-base' : 'h-8 px-2 text-sm',
                className
            )}
            {...props}
        >
            {children}
            <SelectPrimitive.Icon className='flex shrink-0 text-icon-secondary'>
                <CaretDownIcon className={size === 'default' ? 'size-5' : 'size-4'} aria-hidden='true' />
            </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>
    )
}

function SelectValue({ className, ...props }: Omit<SelectPrimitive.Value.Props, 'className'> & { className?: string }) {
    return (
        <SelectPrimitive.Value
            data-slot='select-value'
            className={cn('flex-1 truncate data-placeholder:text-text-placeholder', className)}
            {...props}
        />
    )
}

type SelectContentProps = Omit<SelectPrimitive.Popup.Props, 'className'> & {
    className?: string
    side?: SelectPrimitive.Positioner.Props['side']
    align?: SelectPrimitive.Positioner.Props['align']
    sideOffset?: number
}

function SelectContent({ className, children, side = 'bottom', align = 'start', sideOffset = 4, ...props }: SelectContentProps) {
    return (
        <SelectPrimitive.Portal>
            <SelectPrimitive.Positioner side={side} align={align} sideOffset={sideOffset} alignItemWithTrigger={false} className='z-50'>
                <SelectPrimitive.Popup
                    data-slot='select-content'
                    className={cn(
                        'max-h-(--available-height) w-(--anchor-width) min-w-40 origin-(--transform-origin) overflow-y-auto rounded-lg border border-border-default bg-surface-overlay p-2 text-text-primary shadow-popover outline-none data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
                        className
                    )}
                    {...props}
                >
                    <SelectPrimitive.List>
                        {children}
                    </SelectPrimitive.List>
                </SelectPrimitive.Popup>
            </SelectPrimitive.Positioner>
        </SelectPrimitive.Portal>
    )
}

function SelectItem({ className, children, ...props }: Omit<SelectPrimitive.Item.Props, 'className'> & { className?: string }) {
    return (
        <SelectPrimitive.Item
            data-slot='select-item'
            className={cn(
                'relative flex min-h-10 w-full cursor-pointer items-center gap-2 rounded-md py-2 pr-9 pl-2 text-base text-text-primary outline-none select-none data-disabled:pointer-events-none data-disabled:text-text-disabled data-highlighted:bg-action-secondary-hover dark:data-highlighted:bg-action-secondary-pressed',
                className
            )}
            {...props}
        >
            <SelectPrimitive.ItemText className='flex-1 truncate'>
                {children}
            </SelectPrimitive.ItemText>
            <SelectPrimitive.ItemIndicator className='absolute right-2 flex size-5 items-center justify-center text-icon-brand'>
                <CheckIcon className='size-4' aria-hidden='true' />
            </SelectPrimitive.ItemIndicator>
        </SelectPrimitive.Item>
    )
}

function SelectGroup({ className, ...props }: Omit<SelectPrimitive.Group.Props, 'className'> & { className?: string }) {
    return (
        <SelectPrimitive.Group
            data-slot='select-group'
            className={cn('flex flex-col', className)}
            {...props}
        />
    )
}

function SelectLabel({ className, ...props }: Omit<SelectPrimitive.GroupLabel.Props, 'className'> & { className?: string }) {
    return (
        <SelectPrimitive.GroupLabel
            data-slot='select-label'
            className={cn('px-2 py-1 text-sm font-medium text-text-secondary', className)}
            {...props}
        />
    )
}

function SelectSeparator({ className, ...props }: Omit<SelectPrimitive.Separator.Props, 'className'> & { className?: string }) {
    return (
        <SelectPrimitive.Separator
            data-slot='select-separator'
            className={cn('-mx-2 my-1 h-px bg-border-subtle', className)}
            {...props}
        />
    )
}

export {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectLabel,
    SelectSeparator,
    SelectTrigger,
    SelectValue
}
